import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, Building2, KeyRound, Tag, Inbox, X, ImagePlus, Loader2, ArrowLeft, Download, Star } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatPrice, formatDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PropertyMap } from "@/components/PropertyMap";
import { NewsManager } from "@/components/admin/NewsManager";
import { TeamManager } from "@/components/admin/TeamManager";
import { CITIES, LOGO_DARK } from "@/lib/brand";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RichEditor } from "@/components/admin/RichEditor";
import { Switch } from "@/components/ui/switch";

const TYPES = ["Apartment", "Office", "Shop", "Villa", "Single Family", "Plot", "Apartment Building", "Condominium", "Hotel"];
const CATEGORIES = ["residential", "commercial", "land"];

const EMPTY_FORM = {
  title: "", status: "rent", category: "residential", property_type: "Apartment",
  price: "", price_label: "Per Month", bedrooms: 0, bathrooms: 0, garages: 0, area_sqm: "",
  location: "", city: "Mombasa", lat: -4.0435, lng: 39.6682,
  description: "", featuresText: "", imagesText: "", featured: false, published: true,
};

const Label = ({ children }) => (
  <label className="block text-[11px] font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1.5">{children}</label>
);

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState(null);
  const role = user?.role || "admin";
  const canListings = role === "admin" || role === "listings";
  const TABS = [...(canListings ? ["listings", "inquiries"] : []), "news", ...(role === "admin" ? ["team"] : [])];
  const activeTab = TABS.includes(tab) ? tab : TABS[0];
  const [properties, setProperties] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const fileRef = useRef(null);

  const downloadBackup = async () => {
    setBackingUp(true);
    toast.info("Preparing your backup — this can take a minute for all images.");
    try {
      const res = await api.get("/admin/backup", { responseType: "blob" });
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : "sbs-backup.zip";
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup downloaded to your computer.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Backup failed. Please try again.");
    } finally {
      setBackingUp(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((f) => ({ ...f, imagesText: [f.imagesText.trim(), ...urls].filter(Boolean).join("\n") }));
      toast.success(`${urls.length} photo${urls.length > 1 ? "s" : ""} uploaded.`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const load = useCallback(async () => {
    const safe = (p) => p.then((r) => r.data).catch(() => []);
    const [props, inq] = await Promise.all([safe(api.get("/admin/properties")), safe(api.get("/admin/inquiries"))]);
    setProperties(props);
    setInquiries(inq);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      ...EMPTY_FORM,
      ...p,
      price: p.price ?? "",
      area_sqm: p.area_sqm ?? "",
      featuresText: (p.features || []).join(", "),
      imagesText: (p.images || []).join("\n"),
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      status: form.status,
      category: form.category,
      property_type: form.property_type,
      price: Number(form.price) || 0,
      price_label: form.price_label,
      bedrooms: Number(form.bedrooms) || 0,
      bathrooms: Number(form.bathrooms) || 0,
      garages: Number(form.garages) || 0,
      area_sqm: Number(form.area_sqm) || 0,
      location: form.location,
      city: form.city,
      lat: Number(form.lat) || 0,
      lng: Number(form.lng) || 0,
      description: form.description,
      features: form.featuresText.split(",").map((s) => s.trim()).filter(Boolean),
      images: form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean),
      featured: !!form.featured,
      published: !!form.published,
    };
    try {
      if (editingId) {
        await api.put(`/admin/properties/${editingId}`, payload);
        toast.success("Listing updated.");
      } else {
        await api.post("/admin/properties", payload);
        toast.success("Listing created.");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/properties/${p.id}`);
      toast.success("Listing deleted.");
      setProperties((prev) => prev.filter((x) => x.id !== p.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const togglePublished = async (p) => {
    try {
      const { id, slug, created_at, updated_at, ...rest } = p;
      await api.put(`/admin/properties/${p.id}`, { ...rest, published: !p.published });
      setProperties((prev) => prev.map((x) => (x.id === p.id ? { ...x, published: !p.published } : x)));
      toast.success(p.published ? "Listing unpublished." : "Listing published.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleFeatured = async (p) => {
    try {
      const { id, slug, created_at, updated_at, ...rest } = p;
      await api.put(`/admin/properties/${p.id}`, { ...rest, featured: !p.featured });
      setProperties((prev) => prev.map((x) => (x.id === p.id ? { ...x, featured: !p.featured } : x)));
      toast.success(p.featured ? "Removed from featured." : "Marked as featured.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const setInquiryStatus = async (inq, status) => {
    try {
      const { data } = await api.patch(`/admin/inquiries/${inq.id}`, { status });
      setInquiries((prev) => prev.map((x) => (x.id === inq.id ? data : x)));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const stats = [
    { icon: Building2, label: "Total Listings", value: properties.length, testid: "stat-total-listings" },
    { icon: KeyRound, label: "For Rent", value: properties.filter((p) => p.status === "rent").length, testid: "stat-for-rent" },
    { icon: Tag, label: "For Sale", value: properties.filter((p) => p.status === "sale").length, testid: "stat-for-sale" },
    { icon: Inbox, label: "New Inquiries", value: inquiries.filter((i) => i.status === "new").length, testid: "stat-new-inquiries" },
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-sand">
      <header className="bg-obsidian text-ivory">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-8 py-5">
          <div className="flex items-baseline gap-3">
            <Link to="/" data-testid="admin-home-logo" className="flex items-center gap-3" title="Back to website">
              <img src={LOGO_DARK} alt="SBS Properties" className="h-9 w-auto" />
              <span className="font-serif text-xl font-bold hidden sm:inline">Staff Portal</span>
            </Link>
            <span className="hidden sm:inline text-[10px] font-mono uppercase tracking-[0.2em] text-brand-gold">{role === "admin" ? "Full Access" : role === "listings" ? "Listings Manager" : "News Editor"}</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block text-xs text-ivory/50" data-testid="admin-user-email">{user?.email}</span>
            {role === "admin" && (
              <button
                data-testid="admin-backup-button"
                onClick={downloadBackup}
                disabled={backingUp}
                title="Download a full backup (all data + images) to your computer"
                className="flex items-center gap-1.5 border border-brand-gold/60 text-brand-gold px-3 py-1.5 text-xs font-medium hover:bg-brand-gold hover:text-obsidian transition-colors disabled:opacity-50"
              >
                {backingUp ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
                {backingUp ? "Preparing…" : "Download Backup"}
              </button>
            )}
            <Link to="/" data-testid="admin-view-site-link" className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-xs font-medium hover:border-brand-gold hover:text-brand-gold transition-colors">
              <ArrowLeft className="h-3.5 w-3.5" /> Back to Website
            </Link>
            <button
              data-testid="admin-logout-button"
              onClick={handleLogout}
              className="flex items-center gap-1.5 border border-white/20 px-3 py-1.5 text-xs hover:border-brand-gold hover:text-brand-gold transition-colors"
            >
              <LogOut className="h-3.5 w-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
        {canListings && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-foreground/10 border hairline">
          {stats.map(({ icon: Icon, label, value, testid }) => (
            <div key={label} className="bg-white px-6 py-7" data-testid={testid}>
              <Icon className="h-5 w-5 text-brand" />
              <div className="mt-3 font-serif text-3xl">{loading ? "—" : value}</div>
              <div className="mt-1 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">{label}</div>
            </div>
          ))}
        </div>
        )}

        <div className="mt-10 flex items-center justify-between">
          <div className="flex border hairline bg-white">
            {TABS.map((t) => (
              <button
                key={t}
                data-testid={`admin-tab-${t}`}
                onClick={() => setTab(t)}
                className={`px-6 py-3 text-xs font-mono uppercase tracking-[0.2em] font-bold transition-colors ${
                  activeTab === t ? "bg-brand text-ivory" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          {activeTab === "listings" && (
            <button
              data-testid="admin-add-property-button"
              onClick={openCreate}
              className="flex items-center gap-2 bg-brand px-5 py-3 text-xs font-mono uppercase tracking-[0.15em] font-bold text-ivory hover:bg-brand-hover transition-colors"
            >
              <Plus className="h-4 w-4" /> Add Property
            </button>
          )}
        </div>

        {activeTab === "listings" && (
          <div className="mt-6 border hairline bg-white overflow-x-auto" data-testid="admin-properties-table">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b hairline text-left text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="px-5 py-4">Property</th>
                  <th className="px-5 py-4">Status</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Price</th>
                  <th className="px-5 py-4">City</th>
                  <th className="px-5 py-4">Featured</th>
                  <th className="px-5 py-4">Live</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((p) => (
                  <tr key={p.id} className="border-b hairline last:border-0 hover:bg-ivory/60 transition-colors" data-testid={`admin-property-row-${p.id}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <img src={p.images?.[0] || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=200&q=60"} alt="" className="h-11 w-16 object-cover border hairline" />
                        <div>
                          <div className="font-medium">{p.title}</div>
                          <div className="text-xs text-muted-foreground truncate max-w-[220px]">{p.location}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2.5 py-1 text-[10px] font-mono uppercase tracking-[0.15em] font-bold ${p.status === "rent" ? "bg-brand-emerald/10 text-brand-emerald" : "bg-brand/10 text-brand"}`}>
                        {p.status === "rent" ? "Rent" : "Sale"}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{p.property_type}</td>
                    <td className="px-5 py-3 font-medium whitespace-nowrap">{formatPrice(p.price)}</td>
                    <td className="px-5 py-3 text-muted-foreground">{p.city}</td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        data-testid={`admin-featured-toggle-${p.id}`}
                        onClick={() => toggleFeatured(p)}
                        aria-label={p.featured ? "Remove from featured" : "Mark as featured"}
                        title={p.featured ? "Featured — click to remove" : "Not featured — click to feature"}
                        className={`p-1.5 transition-colors ${p.featured ? "text-brand-gold" : "text-muted-foreground/40 hover:text-brand-gold"}`}
                      >
                        <Star className="h-5 w-5" fill={p.featured ? "currentColor" : "none"} />
                      </button>
                    </td>
                    <td className="px-5 py-3">
                      {role === "admin" ? (
                        <Switch
                          data-testid={`admin-publish-toggle-${p.id}`}
                          checked={p.published}
                          onCheckedChange={() => togglePublished(p)}
                        />
                      ) : (
                        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.15em] ${p.published ? "text-brand-emerald" : "text-muted-foreground/50"}`}>
                          {p.published ? "Live" : "Draft"}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex justify-end gap-2">
                        <button data-testid={`admin-edit-${p.id}`} onClick={() => openEdit(p)} className="border hairline p-2 text-muted-foreground hover:text-brand hover:border-brand transition-colors" aria-label="Edit">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`admin-delete-${p.id}`} onClick={() => remove(p)} className="border hairline p-2 text-muted-foreground hover:text-red-600 hover:border-red-300 transition-colors" aria-label="Delete">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!loading && properties.length === 0 && (
                  <tr><td colSpan={8} className="px-5 py-12 text-center text-muted-foreground">No listings yet — add your first property.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "inquiries" && (
          <div className="mt-6 border hairline bg-white overflow-x-auto" data-testid="admin-inquiries-table">
            <table className="w-full min-w-[820px] text-sm">
              <thead>
                <tr className="border-b hairline text-left text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
                  <th className="px-5 py-4">From</th>
                  <th className="px-5 py-4">Type</th>
                  <th className="px-5 py-4">Message</th>
                  <th className="px-5 py-4">Received</th>
                  <th className="px-5 py-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {inquiries.map((inq) => (
                  <tr key={inq.id} className="border-b hairline last:border-0 align-top" data-testid={`admin-inquiry-row-${inq.id}`}>
                    <td className="px-5 py-4">
                      <div className="font-medium">{inq.name}</div>
                      <div className="text-xs text-muted-foreground">{inq.email}</div>
                      {inq.phone && <div className="text-xs text-muted-foreground">{inq.phone}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <span className="text-xs font-mono uppercase tracking-[0.1em] text-brand font-bold">{inq.inquiry_type}</span>
                      {inq.property_title && <div className="text-xs text-muted-foreground mt-1 max-w-[160px]">{inq.property_title}</div>}
                    </td>
                    <td className="px-5 py-4 text-muted-foreground max-w-[320px]"><p className="line-clamp-3">{inq.message}</p></td>
                    <td className="px-5 py-4 text-xs text-muted-foreground whitespace-nowrap">{formatDate(inq.created_at)}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`admin-inquiry-status-${inq.id}`}
                        value={inq.status}
                        onChange={(e) => setInquiryStatus(inq, e.target.value)}
                        className="border hairline bg-ivory px-2 py-1.5 text-xs outline-none"
                      >
                        <option value="new">New</option>
                        <option value="contacted">Contacted</option>
                        <option value="closed">Closed</option>
                      </select>
                    </td>
                  </tr>
                ))}
                {!loading && inquiries.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-12 text-center text-muted-foreground">No inquiries yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === "news" && (
          <div className="mt-6">
            <NewsManager />
          </div>
        )}

        {activeTab === "team" && (
          <div className="mt-6">
            <TeamManager currentUser={user} />
          </div>
        )}
      </div>

      {/* Property form drawer */}
      {dialogOpen && (
        <div className="fixed inset-0 z-[90] flex justify-end" data-testid="admin-property-dialog">
          <div className="absolute inset-0 bg-obsidian/60 backdrop-blur-sm" onClick={() => setDialogOpen(false)} />
          <div data-lenis-prevent className="relative h-full w-full max-w-2xl bg-ivory overflow-y-auto shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between bg-ivory/95 backdrop-blur border-b hairline px-8 py-5">
              <h2 className="font-serif text-2xl">{editingId ? "Edit Listing" : "New Listing"}</h2>
              <button data-testid="admin-property-close-button" onClick={() => setDialogOpen(false)} className="p-2 text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={save} className="px-8 py-8 space-y-6">
              <div>
                <Label>Title</Label>
                <Input data-testid="admin-property-title-input" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-none bg-white" />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                  <Label>Listing Status</Label>
                  <select data-testid="admin-property-status-select" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm h-9 outline-none">
                    <option value="rent">For Rent</option>
                    <option value="sale">For Sale</option>
                  </select>
                </div>
                <div>
                  <Label>Category</Label>
                  <select data-testid="admin-property-category-select" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm h-9 outline-none">
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Type</Label>
                  <select data-testid="admin-property-type-select" value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="w-full border border-input bg-white px-3 py-2 text-sm h-9 outline-none">
                    {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label>City / Area (type any)</Label>
                  <Input data-testid="admin-property-city-select" list="city-suggestions" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="e.g. Mombasa, Nyali, Diani…" className="rounded-none bg-white" />
                  <datalist id="city-suggestions">
                    {CITIES.map((c) => <option key={c} value={c} />)}
                  </datalist>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Price (KES)</Label>
                  <Input data-testid="admin-property-price-input" type="number" min="0" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="rounded-none bg-white" />
                </div>
                <div>
                  <Label>Price Label</Label>
                  <Input data-testid="admin-property-price-label-input" value={form.price_label} onChange={(e) => setForm({ ...form, price_label: e.target.value })} placeholder="Per Month / Asking Price" className="rounded-none bg-white" />
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {[["bedrooms", "Bedrooms"], ["bathrooms", "Bathrooms"], ["garages", "Parking"], ["area_sqm", "Area (sqm)"]].map(([key, label]) => (
                  <div key={key}>
                    <Label>{label}</Label>
                    <Input data-testid={`admin-property-${key}-input`} type="number" min="0" value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="rounded-none bg-white" />
                  </div>
                ))}
              </div>

              <div>
                <Label>Location / Address</Label>
                <Input data-testid="admin-property-location-input" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Abdel Nasser Road, Mombasa" className="rounded-none bg-white" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Latitude</Label>
                  <Input data-testid="admin-property-lat-input" type="number" step="any" value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} className="rounded-none bg-white" />
                </div>
                <div>
                  <Label>Longitude</Label>
                  <Input data-testid="admin-property-lng-input" type="number" step="any" value={form.lng} onChange={(e) => setForm({ ...form, lng: e.target.value })} className="rounded-none bg-white" />
                </div>
              </div>

              <div>
                <Label>Map Pin — click the map to set the exact location</Label>
                <div className="border hairline overflow-hidden" data-testid="admin-map-picker">
                  <PropertyMap
                    lat={Number(form.lat) || -4.0435}
                    lng={Number(form.lng) || 39.6682}
                    zoom={13}
                    height="260px"
                    onPick={({ lat, lng }) => setForm((f) => ({ ...f, lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)) }))}
                  />
                </div>
              </div>

              <div>
                <Label>Description</Label>
                <RichEditor
                  testId="admin-property-description-input"
                  value={form.description}
                  onChange={(html) => setForm((f) => ({ ...f, description: html }))}
                  placeholder="Describe the property — add photos or a video using the buttons above."
                />
              </div>

              <div>
                <Label>Amenities (comma separated)</Label>
                <Input data-testid="admin-property-features-input" value={form.featuresText} onChange={(e) => setForm({ ...form, featuresText: e.target.value })} placeholder="24/7 Security, Backup Power, Fibre Internet" className="rounded-none bg-white" />
              </div>

              <div>
                <Label>Photos — upload from your computer or phone</Label>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  multiple
                  className="hidden"
                  data-testid="admin-property-file-input"
                  onChange={handleFileUpload}
                />
                <button
                  type="button"
                  data-testid="admin-property-upload-button"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="flex w-full items-center justify-center gap-2 border-2 border-dashed border-brand/40 bg-brand/5 px-4 py-6 text-sm font-semibold text-brand hover:border-brand hover:bg-brand/10 transition-colors disabled:opacity-50"
                >
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  {uploading ? "Uploading…" : "Upload Photos"}
                </button>
                {form.imagesText.trim() && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="admin-property-image-previews">
                    {form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean).map((url, i) => (
                      <div key={`${url}-${i}`} className="relative group">
                        <img src={url} alt="" className="h-16 w-24 object-cover border hairline" />
                        <button
                          type="button"
                          data-testid={`admin-property-image-remove-${i}`}
                          onClick={() => setForm((f) => ({ ...f, imagesText: f.imagesText.split("\n").map((s) => s.trim()).filter((_, j) => j !== i).join("\n") }))}
                          className="absolute -top-2 -right-2 bg-obsidian text-ivory rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                          aria-label="Remove image"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                        {i === 0 && <span className="absolute bottom-0 left-0 bg-brand text-ivory text-[9px] font-mono uppercase px-1.5 py-0.5">Cover</span>}
                      </div>
                    ))}
                  </div>
                )}
                <details className="mt-3">
                  <summary className="text-[11px] font-mono uppercase tracking-[0.15em] text-muted-foreground cursor-pointer hover:text-foreground">Advanced: paste image URLs instead</summary>
                  <Textarea data-testid="admin-property-images-input" rows={3} value={form.imagesText} onChange={(e) => setForm({ ...form, imagesText: e.target.value })} placeholder="https://… (one per line)" className="mt-2 rounded-none bg-white font-mono text-xs" />
                </details>
              </div>

              <div className="flex flex-wrap gap-8 border hairline bg-white px-5 py-4">
                <label className="flex items-center gap-3 text-sm font-medium">
                  <Switch data-testid="admin-property-featured-toggle" checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
                  Featured on homepage
                </label>
                <label className="flex items-center gap-3 text-sm font-medium">
                  <Switch data-testid="admin-property-published-toggle" checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                  Published (visible on site)
                </label>
              </div>

              <div className="sticky bottom-0 bg-ivory/95 backdrop-blur border-t hairline -mx-8 px-8 py-5 flex justify-end gap-3">
                <button type="button" data-testid="admin-property-cancel-button" onClick={() => setDialogOpen(false)} className="border hairline px-6 py-3 text-sm font-medium hover:border-foreground/40 transition-colors">
                  Cancel
                </button>
                <button data-testid="admin-property-save-button" disabled={saving} className="bg-brand px-8 py-3 text-sm font-semibold text-ivory hover:bg-brand-hover transition-colors disabled:opacity-50">
                  {saving ? "Saving…" : editingId ? "Save Changes" : "Create Listing"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
